import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loginUserAction } from '../../store/slices/authSlice';
import {
  CheckCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CreditCardIcon,
  BanknotesIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Import onboarding components
import PlanSelection from './components/PlanSelection';
import CompanySetup from './components/CompanySetup';
import UserSetup from './components/UserSetup';
import PaymentInfo from './components/PaymentInfo';
import OnboardingSuccess from './components/OnboardingSuccess';

// Import services
import { useGetPlansQuery } from '../../services/planApi';
import { useCompleteOnboardingMutation } from '../../services/onboardingApi';

const Onboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    // User data
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    referBy: '',
    acceptTerms: false,

    // Company data
    companyName: '',
    companyCode: '',
    companyAddress: {
      street: '',
      city: '',
      state: '',
      country: 'India',
      zipCode: ''
    },
    companyPhone: '',
    companyEmail: '',
    countryCode: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',

    // Branch data
    branchName: 'Head Office',
    branchCode: '',
    branchAddress: {
      street: '',
      city: '',
      state: '',
      country: 'India',
      zipCode: ''
    },

    // Plan data
    planId: '68b087ff34ac1feb3259cae5', // Default to India Starter plan
    billingCycle: 'yearly',
    selectedPlan: null // Will be populated when plan is selected
  });

  const [errors, setErrors] = useState({});

  // Fetch plans based on selected country
  const { data: plans = [], isLoading: plansLoading, refetch, error: plansError } = useGetPlansQuery(onboardingData.countryCode, {
    skip: !onboardingData.countryCode
  });

  // Debug logging
  console.log('Onboarding Debug:', {
    countryCode: onboardingData.countryCode,
    currency: onboardingData.currency,
    plansCount: plans?.length || 0,
    plansLoading,
    plansError,
    plansData: plans,
    skipQuery: !onboardingData.countryCode,
    plansType: Array.isArray(plans) ? 'array' : typeof plans
  });
  const [completeOnboarding, { isLoading: isCompleting }] = useCompleteOnboardingMutation();

  const handleLogin = () => {
    // This function will be called from the success component
    // For now, redirect to login page with a success message
    navigate('/login', {
      state: {
        message: 'Account created successfully! Please log in with your credentials.',
        email: onboardingData.email
      }
    });
  };

  const steps = [
    {
      id: 1,
      title: 'Company Setup',
      description: 'Set up your company and select your country',
      icon: BuildingOfficeIcon,
      component: CompanySetup
    },
    {
      id: 2,
      title: 'Choose Plan',
      description: 'Select the perfect plan for your business',
      icon: CreditCardIcon,
      component: PlanSelection
    },
    {
      id: 3,
      title: 'Create Account',
      description: 'Create your admin account',
      icon: UserGroupIcon,
      component: UserSetup
    },
    {
      id: 4,
      title: 'Success',
      description: 'Welcome to your HRMS dashboard',
      icon: SparklesIcon,
      component: OnboardingSuccess
    }
  ];

  const currentStepData = steps.find(step => step.id === currentStep);
  const CurrentComponent = currentStepData?.component;

  const handleNext = () => {
    // Clear previous errors
    setErrors({});
    
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1: // Company Setup
        if (!onboardingData.companyName || onboardingData.companyName.trim() === '') {
          newErrors.companyName = 'Company name is required';
        }
        if (!onboardingData.companyCode || onboardingData.companyCode.trim() === '') {
          newErrors.companyCode = 'Company code is required';
        }
        if (!onboardingData.countryCode) {
          newErrors.countryCode = 'Please select your country';
        }
        if (!onboardingData.currency) {
          newErrors.currency = 'Currency is required';
        }
        if (!onboardingData.branchName || onboardingData.branchName.trim() === '') {
          newErrors.branchName = 'Branch name is required';
        }
        if (!onboardingData.branchCode || onboardingData.branchCode.trim() === '') {
          newErrors.branchCode = 'Branch code is required';
        }
        if (!onboardingData.companyPhone || onboardingData.companyPhone.trim() === '') {
          newErrors.companyPhone = 'Company phone is required';
        }
        if (!onboardingData.companyEmail || onboardingData.companyEmail.trim() === '') {
          newErrors.companyEmail = 'Company email is required';
        } else if (!/\S+@\S+\.\S+/.test(onboardingData.companyEmail)) {
          newErrors.companyEmail = 'Please enter a valid email address';
        }
        break;

      case 2: // Plan Selection
        if (!onboardingData.planId) {
          newErrors.planId = 'Please select a plan';
        }
        break;

      // Removed payment validation

      case 4: // User Setup
        if (!onboardingData.email || onboardingData.email.trim() === '') {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(onboardingData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!onboardingData.password) {
          newErrors.password = 'Password is required';
        } else if (onboardingData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character';
        }
        if (!onboardingData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        }
        if (onboardingData.password !== onboardingData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!onboardingData.firstName || onboardingData.firstName.trim() === '') {
          newErrors.firstName = 'First name is required';
        }
        if (!onboardingData.lastName || onboardingData.lastName.trim() === '') {
          newErrors.lastName = 'Last name is required';
        }
        if (!onboardingData.acceptTerms) {
          newErrors.acceptTerms = 'You must accept the Terms of Service and Privacy Policy';
        }
        break;

      default:
        break;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const validateStepData = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Company Setup
        if (!onboardingData.companyName || onboardingData.companyName.trim() === '') {
          return false;
        }
        if (!onboardingData.companyCode || onboardingData.companyCode.trim() === '') {
          return false;
        }
        if (!onboardingData.countryCode) {
          return false;
        }
        break;

      case 2: // Plan Selection
        if (!onboardingData.planId) {
          return false;
        }
        break;

      // Removed payment validation

      case 4: // User Setup
        if (!onboardingData.email || onboardingData.email.trim() === '') {
          return false;
        }
        if (!onboardingData.password) {
          return false;
        }
        if (!onboardingData.firstName || onboardingData.firstName.trim() === '') {
          return false;
        }
        if (!onboardingData.lastName || onboardingData.lastName.trim() === '') {
          return false;
        }
        if (!onboardingData.acceptTerms) {
          return false;
        }
        break;

      default:
        break;
    }

    return true;
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // Clear errors when moving to previous step
      setErrors({});
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDataUpdate = (data) => {
    console.log('handleDataUpdate called with:', data);
    const previousCountry = onboardingData.countryCode;

    setOnboardingData(prev => {
      const newData = {
        ...prev,
        ...data
      };
      console.log('Updated onboardingData:', newData);

      // If country changed, refetch the plans
      if (data.countryCode && data.countryCode !== previousCountry) {
        console.log('Country changed from', previousCountry, 'to', data.countryCode, '- refetching plans');
        setTimeout(() => {
          refetch();
        }, 100);
      }

      return newData;
    });
  };

  const handleComplete = async () => {
    // Clear previous errors
    setErrors({});

    // Validate all steps
    const allStepsValid = [1, 2, 3, 4].every(step => {
      const originalStep = currentStep;
      // Temporarily set current step for validation
      const tempStep = step;
      const tempValidation = validateStepData(tempStep);
      return tempValidation;
    });

    if (!allStepsValid) {
      setErrors({
        general: 'Please complete all required fields before proceeding. You can use the "Previous" button to go back and fix any issues.'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Additional validation for required fields
    const requiredFields = [
      'email', 'password', 'firstName', 'lastName',
      'companyName', 'companyCode', 'branchName', 'branchCode', 'planId'
    ];

    const missingFields = requiredFields.filter(field => 
      !onboardingData[field] || (typeof onboardingData[field] === 'string' && onboardingData[field].trim() === '')
    );

    // Check if terms are accepted
    if (!onboardingData.acceptTerms) {
      setErrors({
        general: 'You must accept the Terms of Service and Privacy Policy to continue.'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate password confirmation
    if (onboardingData.password !== onboardingData.confirmPassword) {
      setErrors({
        general: 'Passwords do not match. Please go back to the User Setup step and ensure both password fields match.'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Additional password validation
    if (onboardingData.password.length < 8) {
      setErrors({
        general: 'Password must be at least 8 characters long. Please go back to the User Setup step and create a stronger password.'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      // Prepare onboarding data for API (flattened structure as expected by backend)
      const onboardingPayload = {
        // User data
        email: onboardingData.email,
        password: onboardingData.password,
        firstName: onboardingData.firstName,
        lastName: onboardingData.lastName,
        referBy: onboardingData.referBy,

        // Company data
        companyName: onboardingData.companyName,
        companyCode: onboardingData.companyCode,
        companyAddress: onboardingData.companyAddress,
        companyPhone: onboardingData.companyPhone,
        companyEmail: onboardingData.companyEmail,
        countryCode: onboardingData.countryCode,
        currency: onboardingData.currency,
        timezone: onboardingData.timezone,

        // Branch data
        branchName: onboardingData.branchName,
        branchCode: onboardingData.branchCode,
        branchAddress: onboardingData.branchAddress,

        // Plan data
        planId: onboardingData.planId,
        billingCycle: onboardingData.billingCycle
      };

      const result = await completeOnboarding(onboardingPayload).unwrap();

      // Handle successful onboarding
      if (result.success) {
        // Store onboarding data for success page
        setOnboardingData(prev => ({
          ...prev,
          selectedPlan: plans.find(p => p._id === onboardingData.planId),
          ...result.data // Include any additional data from backend
        }));

        // Move to success step instead of auto-redirecting
        setCurrentStep(4);
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      console.error('Error details:', {
        status: error.status,
        data: error.data,
        message: error.message
      });

      // Handle backend validation errors
      if (error.data?.message === 'Validation Error' || error.data?.error) {
        const backendErrors = {};

        // Handle array of validation error messages
        if (error.data?.error && Array.isArray(error.data.error)) {
          error.data.error.forEach(err => {
            if (err.includes('email')) {
              if (err.includes('valid email')) {
                backendErrors.email = 'Please enter a valid email address';
              } else if (err.includes('unique')) {
                backendErrors.email = 'This email address is already registered. Please use a different email or try logging in.';
              } else {
                backendErrors.email = err;
              }
            } else if (err.includes('password')) {
              backendErrors.password = 'Password must be at least 8 characters long and meet security requirements';
            } else if (err.includes('firstName')) {
              backendErrors.firstName = 'First name is required';
            } else if (err.includes('lastName')) {
              backendErrors.lastName = 'Last name is required';
            } else if (err.includes('phone')) {
              backendErrors.phone = 'Please enter a valid phone number';
            } else {
              // Generic error for unhandled validation messages
              backendErrors.general = err;
            }
          });
        }

        // Parse Mongoose validation errors from message string (fallback)
        if (error.data?.message?.includes('validation failed')) {
          const errorMessage = error.data.message;
          if (errorMessage.includes('email')) {
            if (errorMessage.includes('unique')) {
              backendErrors.email = 'This email address is already registered. Please use a different email or try logging in.';
            } else {
              backendErrors.email = 'Please enter a valid email address';
            }
          }
          if (errorMessage.includes('firstName')) {
            backendErrors.firstName = 'First name is required';
          }
          if (errorMessage.includes('lastName')) {
            backendErrors.lastName = 'Last name is required';
          }
        }

        // If we have specific field errors, show them
        if (Object.keys(backendErrors).length > 0) {
          setErrors(backendErrors);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }

      // Show user-friendly error message
      const errorMessage = error.data?.message ||
                          error.message ||
                          'Failed to complete onboarding. Please check your information and try again.';

      setErrors({
        general: errorMessage
      });

      // Also show error in alert for debugging
      alert(`Onboarding failed: ${errorMessage}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Onboarding - Innovative HRMS</title>
        <meta name="description" content="Complete your account setup and get started with Innovative HRMS" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4 sm:py-6">
              <div className="flex items-center min-w-0 flex-1">
                <div className="flex-shrink-0">
                  {/* Logo */}
                  <div className="flex justify-center">
                    <img
                      src="/assets/logo.png"
                      alt="Innovative HRMS Logo"
                      className="h-10 w-auto sm:h-14 object-contain"
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl items-center justify-center">
                      <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                  </div>
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Innovative HRMS</h1>
                  <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Human Resource Management System</p>
                </div>
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                <p className="text-xs sm:text-sm text-gray-600">Step {currentStep} of {steps.length}</p>
                <p className="text-xs text-gray-500 truncate max-w-20 sm:max-w-none">{currentStepData?.title}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between overflow-x-auto pb-2">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      step.id <= currentStep
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}>
                      {step.id < currentStep ? (
                        <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                      ) : (
                        <step.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                      )}
                    </div>
                    <div className="mt-2 sm:mt-3 text-center min-w-0">
                      <p className={`text-xs sm:text-sm font-medium truncate max-w-16 sm:max-w-none ${
                        step.id <= currentStep ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 hidden sm:block max-w-20 sm:max-w-24">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-px mx-2 sm:mx-4 transition-colors duration-300 min-w-4 sm:min-w-8 ${
                      step.id < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl overflow-hidden mx-4 sm:mx-0">
            <div className="p-6 sm:p-8 lg:p-12">
              {/* Global Error Display */}
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">
                        There was an error with your submission
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{errors.general}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {CurrentComponent && (
                <CurrentComponent
                  data={onboardingData}
                  onUpdate={handleDataUpdate}
                  plans={plans}
                  plansLoading={plansLoading}
                  errors={errors}
                  setErrors={setErrors}
                  countryCode={onboardingData.countryCode}
                  currency={onboardingData.currency}
                  onLogin={handleLogin}
                />
              )}
            </div>

            {/* Navigation */}
            <div className="px-6 sm:px-8 lg:px-12 pb-6 sm:pb-8 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1 || currentStep === steps.length}
                className={`flex items-center justify-center px-4 sm:px-6 py-3 rounded-xl font-medium transition-all duration-200 order-2 sm:order-1 ${
                  currentStep === 1 || currentStep === steps.length
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-0'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                }`}
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Previous
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="flex items-center justify-center px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg transition-all duration-200 transform hover:scale-105 order-1 sm:order-2"
                >
                  Next
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </button>
              ) : currentStep === steps.length - 1 ? (
                <button
                  onClick={handleComplete}
                  disabled={isCompleting}
                  className="flex items-center justify-center px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
                >
                  {isCompleting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Setting up your account...
                    </>
                  ) : (
                    <>
                      Get Started
                      <CheckCircleIcon className="h-5 w-5 ml-2" />
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Onboarding;
